// Parser for Xbox Series X|S HID input reports.
//
// When macOS claims an Xbox controller via IOHIDManager (which happens
// both for Chromium's Gamepad API and for Web HID), the reports do
// NOT come through as "standard-gamepad" pre-mapped. We get raw HID
// bytes and have to map them ourselves.
//
// This module takes the DataView Chromium passes to `inputreport`
// events and produces the same logical button / axis state shape our
// Phase-1 Gamepad-API backend exposes, so downstream consumers don't
// care which source surfaced the event.
//
// Report layout (report ID 1, 15 bytes of payload after the ID):
//
//   0-1   Left stick X   uint16 LE, centre ~0x8000
//   2-3   Left stick Y   uint16 LE, centre ~0x8000
//   4-5   Right stick X  uint16 LE
//   6-7   Right stick Y  uint16 LE
//   8-9   Left trigger   uint16 LE, 0..1023
//   10-11 Right trigger  uint16 LE, 0..1023
//   12    D-pad hatswitch (0 centre, 1 up, 2 up-right, …, 8 up-left)
//   13    Face + shoulder button bitfield:
//           bit0 A  bit1 B  bit2 X  bit3 Y
//           bit4 LB bit5 RB bit6 View bit7 Menu
//   14    Misc button bitfield:
//           bit0 L3 bit1 R3 bit2 Share
//
// Button indices we emit match the W3C standard mapping so the rest of
// our stack (GAMEPAD_BUTTON constants, keybindings, radial, focus
// bridge) keeps working unchanged.

import { GAMEPAD_BUTTON } from '../game/keybindings';

const TRIGGER_THRESHOLD = 50;        // trigger value (0..1023) above which we count as pressed
const AXIS_CENTER = 0x8000;
const AXIS_RANGE  = 0x8000;
const AXIS_DEAD_ZONE = 0.10;         // applied after normalisation; bridge has its own so keep light
const DPAD_UP_SET    = new Set([1, 2, 8]);
const DPAD_RIGHT_SET = new Set([2, 3, 4]);
const DPAD_DOWN_SET  = new Set([4, 5, 6]);
const DPAD_LEFT_SET  = new Set([6, 7, 8]);

function normAxis(raw) {
  let v = (raw - AXIS_CENTER) / AXIS_RANGE;
  if (v > 1) v = 1; if (v < -1) v = -1;
  if (Math.abs(v) < AXIS_DEAD_ZONE) v = 0;
  return v;
}

// Given the raw 15-byte report, produce `{ buttons: Array<boolean
// length 16>, axes: [lx, ly, rx, ry] }`. Consumers diff against the
// previous snapshot to derive button-down / button-up events.
export function parseXboxReport(view) {
  if (!view || view.byteLength < 15) return null;

  const lx = view.getUint16(0, true);
  const ly = view.getUint16(2, true);
  const rx = view.getUint16(4, true);
  const ry = view.getUint16(6, true);
  const lt = view.getUint16(8, true);
  const rt = view.getUint16(10, true);
  const dpad = view.getUint8(12);
  const b1 = view.getUint8(13);
  const b2 = view.getUint8(14);

  const buttons = new Array(16).fill(false);
  buttons[GAMEPAD_BUTTON.A]     = Boolean(b1 & 0x01);
  buttons[GAMEPAD_BUTTON.B]     = Boolean(b1 & 0x02);
  buttons[GAMEPAD_BUTTON.X]     = Boolean(b1 & 0x04);
  buttons[GAMEPAD_BUTTON.Y]     = Boolean(b1 & 0x08);
  buttons[GAMEPAD_BUTTON.LB]    = Boolean(b1 & 0x10);
  buttons[GAMEPAD_BUTTON.RB]    = Boolean(b1 & 0x20);
  buttons[GAMEPAD_BUTTON.BACK]  = Boolean(b1 & 0x40);
  buttons[GAMEPAD_BUTTON.START] = Boolean(b1 & 0x80);
  buttons[GAMEPAD_BUTTON.L3]    = Boolean(b2 & 0x01);
  buttons[GAMEPAD_BUTTON.R3]    = Boolean(b2 & 0x02);
  buttons[GAMEPAD_BUTTON.LT]    = lt > TRIGGER_THRESHOLD;
  buttons[GAMEPAD_BUTTON.RT]    = rt > TRIGGER_THRESHOLD;
  buttons[GAMEPAD_BUTTON.DPAD_UP]    = DPAD_UP_SET.has(dpad);
  buttons[GAMEPAD_BUTTON.DPAD_RIGHT] = DPAD_RIGHT_SET.has(dpad);
  buttons[GAMEPAD_BUTTON.DPAD_DOWN]  = DPAD_DOWN_SET.has(dpad);
  buttons[GAMEPAD_BUTTON.DPAD_LEFT]  = DPAD_LEFT_SET.has(dpad);

  // Standard-mapping axis order: left-X, left-Y, right-X, right-Y.
  // Y axes invert: HID reports "up" as a small value, standard
  // mapping reports "up" as negative. (raw - center) / range already
  // does it naturally for X, but HID Y increases downward on macOS so
  // we keep the same formula; if a future pad inverts this we can
  // flip per-vendor here.
  const axes = [normAxis(lx), normAxis(ly), normAxis(rx), normAxis(ry)];

  return { buttons, axes };
}

export function diffReports(prev, next) {
  // Returns { buttonChanges: [{index, pressed}], axisChanged: boolean }
  const out = { buttonChanges: [], axisChanged: false, nextSnapshot: next };
  if (!next) return out;
  if (!prev) {
    for (let i = 0; i < next.buttons.length; i++) {
      if (next.buttons[i]) out.buttonChanges.push({ index: i, pressed: true });
    }
    out.axisChanged = true;
    return out;
  }
  for (let i = 0; i < next.buttons.length; i++) {
    if (Boolean(prev.buttons[i]) !== Boolean(next.buttons[i])) {
      out.buttonChanges.push({ index: i, pressed: next.buttons[i] });
    }
  }
  for (let i = 0; i < 4; i++) {
    if (prev.axes[i] !== next.axes[i]) { out.axisChanged = true; break; }
  }
  return out;
}
